import { bibleBooks, resolveBibleBook } from './bible';

// ─── Types ───────────────────────────────────────────────────────────────────

export type CommentarySourceId = 'henry' | 'jfb' | 'clarke' | 'gill' | 'guzik' | 'smith' | 'robertson';

export interface CommentarySourceMeta {
  id: CommentarySourceId;
  label: string;
  shortLabel: string;
  provider: 'helloao' | 'proxy';
  otOnly?: boolean;
  ntOnly?: boolean;
  attributionUrl?: string;
}

export interface CommentaryVerse {
  verseNumber: number;
  text: string;           // full text (used for insert / Guzik compat)
  paragraphs?: string[];  // split content for multi-paragraph display
}

export interface CommentaryChapter {
  source: CommentarySourceId;
  sourceName: string;
  book: string;
  bookId: string;
  chapter: number;
  verses: CommentaryVerse[];
  fetchedAt: number;
}

export interface ParsedPassage {
  bookName: string;
  bookId: string;
  chapter: number;
  verse: number | null;
}

interface HelloAOVerseContent {
  type: 'verse' | string;
  number: number;
  content: string[];
}

interface HelloAOResponse {
  chapter?: {
    content?: HelloAOVerseContent[];
  };
}

// ─── Source Registry ─────────────────────────────────────────────────────────

export const COMMENTARY_SOURCES: CommentarySourceMeta[] = [
  { id: 'henry',     label: 'Matthew Henry',              shortLabel: 'Henry',      provider: 'helloao' },
  { id: 'guzik',     label: 'David Guzik',                shortLabel: 'Guzik',      provider: 'proxy',  attributionUrl: 'https://enduringword.com/bible-commentary/' },
  { id: 'smith',     label: 'Chuck Smith',                shortLabel: 'Smith',      provider: 'proxy',  attributionUrl: 'https://www.blueletterbible.org/commentaries/smith_chuck/' },
  { id: 'robertson', label: 'A.T. Robertson',             shortLabel: 'Robertson',  provider: 'proxy',  ntOnly: true, attributionUrl: 'https://www.studylight.org/commentaries/rwp/' },
  { id: 'jfb',       label: 'Jamieson-Fausset-Brown',     shortLabel: 'JFB',        provider: 'helloao' },
  { id: 'clarke',    label: 'Adam Clarke',                shortLabel: 'Clarke',     provider: 'helloao' },
  { id: 'gill',      label: 'John Gill',                  shortLabel: 'Gill',       provider: 'helloao' },
];

const NT_BOOK_IDS = new Set([
  'MAT','MRK','LUK','JHN','ACT','ROM','1CO','2CO','GAL','EPH','PHP','COL',
  '1TH','2TH','1TI','2TI','TIT','PHM','HEB','JAS','1PE','2PE','1JN','2JN',
  '3JN','JUD','REV',
]);

export function isNTBook(bookId: string): boolean {
  return NT_BOOK_IDS.has(bookId.toUpperCase());
}

// ─── Passage Parser ───────────────────────────────────────────────────────────

export function parsePassage(raw: string): ParsedPassage | null {
  if (!raw?.trim()) return null;

  // Periods are punctuation inside book names ("Ps." / "1 Cor."), not separators.
  const normalized = raw.trim().replace(/\./g, ' ').replace(/\s+/g, ' ');

  // Match: optional leading digit + book name words + chapter + optional :verse[-verse]
  const match = normalized.match(
    /^((?:\d\s*)?(?:[A-Za-z]+\s*)+?)\s+(\d+)(?:\s*[-\u2013]\s*\d+)?(?::(\d+)(?:\s*[-\u2013]\s*\d+)?)?$/
  );
  if (!match) return null;

  const [, bookRaw, chapterStr, verseStr] = match;
  const chapter = parseInt(chapterStr, 10);
  if (!chapter) return null;

  const verse = verseStr ? parseInt(verseStr, 10) : null;

  // Resolves aliases too — "Psalm 42", "Ps 42", "1Corinthians 13", "I John 1".
  const book = resolveBibleBook(bookRaw);
  if (!book) return null;

  return { bookName: book.name, bookId: book.id, chapter, verse };
}

// ─── Cache ────────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 60 * 60 * 1000;
const commentaryCache = new Map<string, CommentaryChapter>();

function cacheKey(source: CommentarySourceId, bookId: string, chapter: number) {
  return `${source}:${bookId.toUpperCase()}:${chapter}`;
}

function getCached(source: CommentarySourceId, bookId: string, chapter: number): CommentaryChapter | null {
  const entry = commentaryCache.get(cacheKey(source, bookId, chapter));
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    commentaryCache.delete(cacheKey(source, bookId, chapter));
    return null;
  }
  return entry;
}

function setCache(data: CommentaryChapter) {
  commentaryCache.set(cacheKey(data.source, data.bookId, data.chapter), data);
}

// ─── Strip HTML helper ────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ─── Commentary paragraph splitter ───────────────────────────────────────────
// Classical commentaries (Henry, Gill, JFB, Clarke) use inline section markers
// rather than blank lines. This detects those markers and splits there.

export function splitCommentaryText(raw: string): string[] {
  if (!raw.trim()) return [];

  // 1. Try double-newline breaks (ideal but API rarely provides them)
  if (/\n\n/.test(raw)) {
    const parts = raw.split(/\n\n+/).map(s => s.replace(/\n/g, ' ').trim()).filter(Boolean);
    if (parts.length > 1) return parts;
  }

  // 2. Strip hard-wrap single newlines (they're word-wrap artifacts, not breaks)
  const flat = raw.replace(/\n/g, ' ').replace(/  +/g, ' ').trim();

  // 3. Insert breaks at classical commentary section markers:
  //    - Roman numeral sections after sentence end:  ". I. " / "; II. "
  //    - Arabic numbered points after sentence end:  ". 1. " / ", 1. "
  //    - Parenthetical counters:                     ". (1.) " / "(2.) "
  //    Require the next word to start with a capital to avoid false splits
  //    on Bible references like "Acts 2:1" or "Ps. iv."
  const marked = flat
    // Uppercase Roman numerals: I. II. III. IV. V. VI. VII. VIII. IX. X. XI. XII.
    // (Henry, Gill, JFB, Clarke)
    .replace(
      /([.;,])\s+((?:I{1,3}|IV|VI{0,3}|IX|X{1,2}I{0,3}|XI{0,3})\.\s+)(?=[A-Z"])/g,
      '$1\n\n$2'
    )
    // Arabic numbered points: "1. " "2. " — require sentence-end punctuation before
    .replace(
      /([.;])\s+(\d{1,2}\.\s+)(?=[A-Z"])/g,
      '$1\n\n$2'
    )
    // Parenthetical: (1.) (2.) etc.
    .replace(
      /\s+(\(\d{1,2}\.?\))\s+(?=[A-Z"])/g,
      '\n\n$1 '
    )
    // Guzik-style lowercase outline letters: ". a. The" ". b. Another"
    // Only a–h to avoid matching common words; also allow " after the marker
    .replace(
      /([.;)])\s+([a-h]\.\s+)(?=[A-Z"])/g,
      '$1\n\n$2'
    )
    // Guzik-style lowercase Roman numerals: ". i. " ". ii. " ". iii. " ". iv. "
    .replace(
      /([.;)])\s+((?:i{1,3}|iv|vi{0,3}|ix|x{0,2}i{0,3})\.\s+)(?=[A-Z"])/g,
      '$1\n\n$2'
    );

  if (marked !== flat) {
    const parts = marked.split(/\n\n+/).map(s => s.trim()).filter(s => s.length > 10);
    if (parts.length > 1) return parts;
  }

  return [flat];
}

// ─── HelloAO Fetcher (proxied through Supabase edge function) ────────────────

const HELLOAO_SLUG: Record<Exclude<CommentarySourceId, 'guzik' | 'smith' | 'robertson'>, string> = {
  henry:  'matthew-henry',
  jfb:    'jamieson-fausset-brown',
  clarke: 'adam-clarke',
  gill:   'john-gill',
};

export async function fetchHelloAOCommentary(
  sourceId: Exclude<CommentarySourceId, 'guzik' | 'smith' | 'robertson'>,
  bookId: string,
  chapter: number,
  supabaseUrl: string,
  accessToken: string,
  anonKey: string
): Promise<CommentaryChapter> {
  const cached = getCached(sourceId, bookId, chapter);
  if (cached) return cached;

  const slug = HELLOAO_SLUG[sourceId];
  const response = await fetch(`${supabaseUrl}/functions/v1/generate-commentary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ mode: 'helloao', source: slug, bookId: bookId.toUpperCase(), chapter }),
  });

  const json = await response.json().catch(() => ({}));

  if (response.status === 404) {
    throw new Error('This commentary does not cover this passage.');
  }
  if (!response.ok) {
    throw new Error(json.error || `Failed to load commentary (HTTP ${response.status})`);
  }
  if (!json.success) {
    throw new Error(json.error || 'No commentary available for this passage.');
  }

  const helloaoJson: HelloAOResponse = json.data;

  const rawVerses = helloaoJson.chapter?.content?.filter(v => v.type === 'verse') ?? [];

  if (rawVerses.length === 0) {
    throw new Error('No commentary available for this passage.');
  }

  const sourceMeta = COMMENTARY_SOURCES.find(s => s.id === sourceId)!;
  const book = bibleBooks.find(b => b.id === bookId.toUpperCase())!;

  const result: CommentaryChapter = {
    source: sourceId,
    sourceName: sourceMeta.label,
    book: book.name,
    bookId: bookId.toUpperCase(),
    chapter,
    verses: rawVerses
      .filter(v => v.content?.length > 0)
      .map(v => {
        const text = stripHtml(v.content.join(' '));
        const paragraphs = splitCommentaryText(text);
        return {
          verseNumber: v.number,
          text,
          paragraphs: paragraphs.length > 1 ? paragraphs : undefined,
        };
      }),
    fetchedAt: Date.now(),
  };

  setCache(result);
  return result;
}

// ─── Guzik Fetcher (via Supabase Edge Function) ───────────────────────────────

export async function fetchGuzikCommentary(
  bookId: string,
  chapter: number,
  supabaseUrl: string,
  accessToken: string,
  anonKey: string
): Promise<CommentaryChapter> {
  const cached = getCached('guzik', bookId, chapter);
  if (cached) return cached;

  const book = bibleBooks.find(b => b.id === bookId.toUpperCase());
  if (!book) throw new Error('Unknown book');

  const passage = `${book.name} ${chapter}`;

  const response = await fetch(`${supabaseUrl}/functions/v1/generate-commentary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ passage, source: 'david-guzik', mode: 'raw' }),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.error || `HTTP ${response.status}`);
  }

  if (!json.success || !json.verses?.length) {
    throw new Error(json.error || 'Commentary not available for this passage.');
  }

  const result: CommentaryChapter = {
    source: 'guzik',
    sourceName: 'David Guzik',
    book: book.name,
    bookId: bookId.toUpperCase(),
    chapter,
    verses: json.verses.map((v: CommentaryVerse) => {
      const paragraphs = splitCommentaryText(v.text || '');
      return { ...v, paragraphs: paragraphs.length > 1 ? paragraphs : undefined };
    }),
    fetchedAt: Date.now(),
  };

  setCache(result);
  return result;
}

// ─── Robertson Fetcher (via Supabase Edge Function → StudyLight scraper) ─────

export async function fetchRobertsonCommentary(
  bookId: string,
  chapter: number,
  supabaseUrl: string,
  accessToken: string,
  anonKey: string
): Promise<CommentaryChapter> {
  const cached = getCached('robertson', bookId, chapter);
  if (cached) return cached;

  const book = bibleBooks.find(b => b.id === bookId.toUpperCase());
  if (!book) throw new Error('Unknown book');

  const passage = `${book.name} ${chapter}`;

  const response = await fetch(`${supabaseUrl}/functions/v1/generate-commentary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ passage, source: 'robertson', mode: 'raw' }),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.error || `HTTP ${response.status}`);
  }

  if (!json.success || !json.verses?.length) {
    throw new Error(json.error || 'Commentary not available for this passage.');
  }

  const result: CommentaryChapter = {
    source: 'robertson',
    sourceName: 'A.T. Robertson',
    book: book.name,
    bookId: bookId.toUpperCase(),
    chapter,
    verses: json.verses.map((v: CommentaryVerse) => {
      const paragraphs = splitCommentaryText(v.text || '');
      return { ...v, paragraphs: paragraphs.length > 1 ? paragraphs : undefined };
    }),
    fetchedAt: Date.now(),
  };

  setCache(result);
  return result;
}

// ─── Chuck Smith Fetcher (via Supabase Edge Function → BLB scraper) ───────────

export async function fetchChuckSmithCommentary(
  bookId: string,
  chapter: number,
  supabaseUrl: string,
  accessToken: string,
  anonKey: string
): Promise<CommentaryChapter> {
  const cached = getCached('smith', bookId, chapter);
  if (cached) return cached;

  const book = bibleBooks.find(b => b.id === bookId.toUpperCase());
  if (!book) throw new Error('Unknown book');

  const passage = `${book.name} ${chapter}`;

  const response = await fetch(`${supabaseUrl}/functions/v1/generate-commentary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ passage, source: 'chuck-smith', mode: 'raw' }),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(json.error || `HTTP ${response.status}`);
  }

  if (!json.success || !json.verses?.length) {
    throw new Error(json.error || 'Commentary not available for this passage.');
  }

  const result: CommentaryChapter = {
    source: 'smith',
    sourceName: 'Chuck Smith',
    book: book.name,
    bookId: bookId.toUpperCase(),
    chapter,
    verses: json.verses.map((v: CommentaryVerse) => {
      const paragraphs = splitCommentaryText(v.text || '');
      return { ...v, paragraphs: paragraphs.length > 1 ? paragraphs : undefined };
    }),
    fetchedAt: Date.now(),
  };

  setCache(result);
  return result;
}
